<app-offcanvas-sheet
  [visible]="visible"
  [title]="title"
  [saveLabel]="isAddingQuantity ? 'Add Quantity' : (isEditing ? 'Save Changes' : 'Add Item')"
  (closeCanvas)="onClose()"
  (save)="onSave()">

  <!-- ── ADD QUANTITY MODE ── -->
  <ng-container *ngIf="isAddingQuantity">
    <div class="form-group-mobile">
      <label class="label-with-tip">Item Name</label>
      <div class="form-control" style="background:#f8f9fa; color:#6c757d;">{{ editData.itemName }}</div>
    </div>
    <div class="form-group-mobile">
      <label class="label-with-tip">Current Quantity</label>
      <div class="form-control" style="background:#f8f9fa; color:#6c757d;">{{ editData.quantity }}</div>
    </div>
    <div class="form-group-mobile">
      <label class="label-with-tip">Quantity to Add *</label>
      <input type="number" class="form-control" [(ngModel)]="editData.quantityToAdd" min="1" step="1" />
    </div>
  </ng-container>

  <!-- ── RAW MATERIAL MODE ── -->
  <ng-container *ngIf="mode === 'raw' && !isAddingQuantity">

    <div class="inv-section-label">Item Details</div>

    <div class="form-group-mobile">
      <label class="label-with-tip">Item Name *
        <app-tooltip [tooltipId]="'mi-raw-name'">
          <i icon class="bi bi-info-circle"></i>
          <div content><p>The name of the raw material as you want it shown across the app (e.g. "Steel Rod 10mm").</p></div>
        </app-tooltip>
      </label>
      <input type="text" class="form-control" [(ngModel)]="editData.itemName" placeholder="e.g. Steel Rod 10mm" />
    </div>
    <div class="form-group-mobile">
      <label class="label-with-tip">SKU / Code
        <app-tooltip [tooltipId]="'mi-raw-sku'">
          <i icon class="bi bi-info-circle"></i>
          <div content><p>A short code that uniquely identifies this item. Leave blank to auto-generate one.</p></div>
        </app-tooltip>
      </label>
      <input type="text" class="form-control" [(ngModel)]="editData.itemCode" placeholder="Auto-generated if blank" />
    </div>
    <div class="form-group-mobile">
      <label class="label-with-tip">Category *
        <app-tooltip [tooltipId]="'mi-raw-category'">
          <i icon class="bi bi-info-circle"></i>
          <div content><p>Groups items for filtering and reporting (e.g. "Metals", "Packaging"). Manage categories in Configuration.</p></div>
        </app-tooltip>
      </label>
      <app-single-select-dropdown
        [options]="categoryOptions"
        [selectedValue]="editData.categoryID != null ? String(editData.categoryID) : null"
        placeholder="Select category"
        [allowAddNew]="true"
        [addNewLabel]="'Add new category'"
        (selectedValueChange)="editData.categoryID = fromValue($event)"
        (addMore)="onAddNewCategory()">
      </app-single-select-dropdown>
    </div>
    <div class="form-group-mobile">
      <label class="label-with-tip">Unit of Measure *
        <app-tooltip [tooltipId]="'mi-raw-uom'">
          <i icon class="bi bi-info-circle"></i>
          <div content><p>A standard unit used to count or quantify something — like using meters (m) to measure length.</p></div>
        </app-tooltip>
      </label>
      <app-single-select-dropdown
        [options]="uomOptions"
        [selectedValue]="editData.purchaseUOMID != null ? String(editData.purchaseUOMID) : null"
        placeholder="Select UOM"
        (selectedValueChange)="editData.purchaseUOMID = fromValue($event)">
      </app-single-select-dropdown>
    </div>
    <!-- Purchase Price (add mode: standalone; edit mode: paired with Purchase Qty) -->
    <div *ngIf="!isEditing" class="form-group-mobile">
      <label class="label-with-tip">Purchase Price *
        <app-tooltip [tooltipId]="'mi-raw-price'">
          <i icon class="bi bi-info-circle"></i>
          <div content><p>The price you pay your supplier for one purchase unit of this item.</p></div>
        </app-tooltip>
      </label>
      <input type="number" class="form-control" [(ngModel)]="editData.purchasePrice" (ngModelChange)="onPurchasePriceChange()" min="0" step="0.01" appNumericInput />
      <div class="text-danger small mt-1" *ngIf="purchasePriceError">{{ purchasePriceError }}</div>
    </div>

    <div *ngIf="isEditing" class="form-row-2">
      <div class="form-group-mobile">
        <label class="label-with-tip">Purchase Price *
          <app-tooltip [tooltipId]="'mi-raw-price-edit'">
            <i icon class="bi bi-info-circle"></i>
            <div content><p>The price you pay your supplier for one purchase unit. Changing this records a new stock receipt.</p></div>
          </app-tooltip>
        </label>
        <input type="number" class="form-control" [(ngModel)]="editData.purchasePrice" (ngModelChange)="onPurchasePriceChange()" min="0" step="0.01" appNumericInput />
        <div class="text-danger small mt-1" *ngIf="purchasePriceError">{{ purchasePriceError }}</div>
      </div>
      <div class="form-group-mobile">
        <label class="label-with-tip">Purchase Qty *
          <app-tooltip [tooltipId]="'mi-raw-qty-edit'">
            <i icon class="bi bi-info-circle"></i>
            <div content><p>How many units you receive per purchase. Changing this records a new stock receipt.</p></div>
          </app-tooltip>
        </label>
        <input type="number" class="form-control" [(ngModel)]="editData.purchaseQuantity" (ngModelChange)="onPurchaseQtyChange()" min="0.01" step="0.01" />
      </div>
    </div>

    <!-- Unit Cost (read-only, auto-calculated) -->
    <div *ngIf="isEditing" class="form-group-mobile">
      <label class="label-with-tip">Unit Cost</label>
      <input type="number" class="form-control" [ngModel]="editData.unitCost" readonly style="background:#f8f9fa; color:#6c757d;" />
      <small class="text-muted ms-3" style="font-size:0.72rem;">Calculated as purchase price ÷ purchase qty — changing price or qty records a new stock receipt</small>
    </div>

    <!-- Initial Quantity + Reorder Level only shown when adding (not editing) -->
    <div *ngIf="!isEditing" class="form-row-2">
      <div class="form-group-mobile">
        <label class="label-with-tip">Initial Quantity
          <app-tooltip [tooltipId]="'mi-raw-initial'">
            <i icon class="bi bi-info-circle"></i>
            <div content><p>How much stock you have on hand right now. Used as the starting balance.</p></div>
          </app-tooltip>
        </label>
        <app-area-quantity-input
          [uomCode]="getUomCode(editData.purchaseUOMID)"
          [(value)]="editData.quantityToAdd"
          cssClass="form-control"
          (valueChange)="onInitialQuantityChange()"
        />
      </div>
      <div class="form-group-mobile">
        <label class="label-with-tip">Reorder Level *
          <app-tooltip [tooltipId]="'mi-raw-reorder'">
            <i icon class="bi bi-info-circle"></i>
            <div content><p>When stock falls to this quantity the item is flagged as low stock so you know to reorder.</p></div>
          </app-tooltip>
        </label>
        <input type="number" class="form-control" [(ngModel)]="editData.lowStockThreshold" min="0" step="1" />
      </div>
    </div>

    <!-- Unit Cost (read-only, auto-calculated from purchase price ÷ initial quantity) -->
    <div *ngIf="!isEditing" class="form-group-mobile">
      <label class="label-with-tip">Unit Cost</label>
      <input type="number" class="form-control" [ngModel]="editData.unitCost" readonly style="background:#f8f9fa; color:#6c757d;" />
      <small class="text-muted ms-3" style="font-size:0.72rem;">Calculated as purchase price ÷ initial quantity</small>
    </div>

    <!-- Reorder Level alone in edit mode -->
    <div *ngIf="isEditing" class="form-group-mobile">
      <label class="label-with-tip">Reorder Level *
        <app-tooltip [tooltipId]="'mi-raw-reorder-edit'">
          <i icon class="bi bi-info-circle"></i>
          <div content><p>When stock falls to this quantity the item is flagged as low stock so you know to reorder.</p></div>
        </app-tooltip>
      </label>
      <input type="number" class="form-control" [(ngModel)]="editData.lowStockThreshold" min="0" step="1" />
    </div>

    <div class="inv-section-label mt-2">Supplier *</div>
    <div class="form-group-mobile">
      <label class="label-with-tip">Supplier
        <app-tooltip [tooltipId]="'mi-raw-supplier'">
          <i icon class="bi bi-info-circle"></i>
          <div content><p>The default supplier you buy this raw material from. Manage suppliers in Configuration.</p></div>
        </app-tooltip>
      </label>
      <app-single-select-dropdown
        [options]="supplierOptions"
        [selectedValue]="editData.supplierID != null ? String(editData.supplierID) : null"
        placeholder="Select supplier"
        [allowAddNew]="true"
        [addNewLabel]="'Add new supplier'"
        (selectedValueChange)="editData.supplierID = fromValue($event)"
        (addMore)="onAddNewSupplier()">
      </app-single-select-dropdown>
    </div>

    <!-- Buyout Flag -->
    <div class="form-group-mobile mt-3">
      <div class="form-check">
        <input type="checkbox" class="form-check-input" id="isBuyout" [(ngModel)]="editData.isBuyout" />
        <label class="form-check-label" for="isBuyout">
          Is Buyout Item
          <app-tooltip [tooltipId]="'mi-raw-buyout'">
            <i icon class="bi bi-info-circle"></i>
            <div content><p>Mark this as a buyout item if it's purchased ready-made rather than manufactured in-house.</p></div>
          </app-tooltip>
        </label>
      </div>
    </div>

  </ng-container>

  <!-- ── PRODUCT MODE ── -->
  <ng-container *ngIf="mode === 'product' && !isAddingQuantity">

    <div class="inv-section-label">Product Details</div>

    <div class="form-group-mobile">
      <label class="label-with-tip">Product Name *
        <app-tooltip [tooltipId]="'mi-prod-name'">
          <i icon class="bi bi-info-circle"></i>
          <div content><p>The name of the finished product as it appears on invoices and reports.</p></div>
        </app-tooltip>
      </label>
      <input type="text" class="form-control" [(ngModel)]="editData.itemName" placeholder="e.g. Finished Widget" />
    </div>
    <div class="form-group-mobile">
      <label class="label-with-tip">SKU / Code
        <app-tooltip [tooltipId]="'mi-prod-sku'">
          <i icon class="bi bi-info-circle"></i>
          <div content><p>A short code that uniquely identifies this product. Leave blank to auto-generate one.</p></div>
        </app-tooltip>
      </label>
      <input type="text" class="form-control" [(ngModel)]="editData.itemCode" placeholder="Auto-generated if blank" />
    </div>
    <div class="form-group-mobile">
      <label class="label-with-tip">Category *
        <app-tooltip [tooltipId]="'mi-prod-category'">
          <i icon class="bi bi-info-circle"></i>
          <div content><p>Groups products for filtering and reporting. Manage categories in Configuration.</p></div>
        </app-tooltip>
      </label>
      <app-single-select-dropdown
        [options]="categoryOptions"
        [selectedValue]="editData.categoryID != null ? String(editData.categoryID) : null"
        placeholder="Select category"
        [allowAddNew]="true"
        [addNewLabel]="'Add new category'"
        (selectedValueChange)="editData.categoryID = fromValue($event)"
        (addMore)="onAddNewCategory()">
      </app-single-select-dropdown>
    </div>
    <div class="form-group-mobile">
      <label class="label-with-tip">Unit of Measure *
        <app-tooltip [tooltipId]="'mi-prod-uom'">
          <i icon class="bi bi-info-circle"></i>
          <div content><p>The unit you sell this product in (e.g. each, kg, box).</p></div>
        </app-tooltip>
      </label>
      <app-single-select-dropdown
        [options]="uomOptions"
        [selectedValue]="editData.purchaseUOMID != null ? String(editData.purchaseUOMID) : null"
        placeholder="Select UOM"
        (selectedValueChange)="editData.purchaseUOMID = fromValue($event)">
      </app-single-select-dropdown>
    </div>
    <div class="form-group-mobile">
      <label class="label-with-tip">Selling Price
        <app-tooltip [tooltipId]="'mi-prod-price'">
          <i icon class="bi bi-info-circle"></i>
          <div content><p>The price you charge customers for one unit of this product.</p></div>
        </app-tooltip>
      </label>
      <input type="number" class="form-control" [(ngModel)]="editData.sellingPrice" min="0" step="0.01" />
    </div>
    <!-- BOM Section -->
    <div class="inv-section-label mt-2">Bill of Materials
      <app-tooltip [tooltipId]="'mi-prod-bom'">
        <i icon class="bi bi-info-circle"></i>
        <div content><p>The raw materials and quantities needed to make one unit of this product. The total cost is the sum of (qty × purchase price) of each component.</p></div>
      </app-tooltip>
      <span *ngIf="bomTotalCost > 0" class="bom-total-badge">Cost: R {{ bomTotalCost.toFixed(2) }}</span>
    </div>

    <div *ngFor="let comp of bomComponents; let i = index" class="bom-row">
      <app-single-select-dropdown
        [options]="rawMaterialOptions"
        [selectedValue]="comp.rawMaterialItemID != null ? String(comp.rawMaterialItemID) : null"
        placeholder="Select raw material"
        (selectedValueChange)="comp.rawMaterialItemID = fromValue($event); onBOMRawMaterialChange(i)">
      </app-single-select-dropdown>
      <input type="number" class="form-control bom-qty"
        [(ngModel)]="comp.quantityRequired"
        (ngModelChange)="recalcBOMCost()"
        min="0.001" step="0.001" placeholder="Qty" />
      <span class="bom-uom">{{ getUomCode(comp.componentUOMID) }}</span>
      <button class="bom-remove-btn" (click)="removeBOMComponent(i)" type="button">
        <i class="bi bi-trash3"></i>
      </button>
    </div>

    <button class="bom-add-btn" (click)="addBOMComponent()" type="button">
      <i class="bi bi-plus-circle me-1"></i>Add Component
    </button>

  </ng-container>

  <!-- ── SERVICE MODE ── -->
  <ng-container *ngIf="mode === 'service' && !isAddingQuantity">

    <div class="inv-section-label">Service Details</div>

    <div class="form-group-mobile">
      <label class="label-with-tip">Service Name *
        <app-tooltip [tooltipId]="'mi-svc-name'">
          <i icon class="bi bi-info-circle"></i>
          <div content><p>The name of the service as it appears on invoices and reports.</p></div>
        </app-tooltip>
      </label>
      <input type="text" class="form-control" [(ngModel)]="editData.itemName" placeholder="e.g. Installation Labour" />
    </div>
    <div class="form-group-mobile">
      <label class="label-with-tip">Category *
        <app-tooltip [tooltipId]="'mi-svc-category'">
          <i icon class="bi bi-info-circle"></i>
          <div content><p>Groups services for filtering and reporting. Manage categories in Configuration.</p></div>
        </app-tooltip>
      </label>
      <app-single-select-dropdown
        [options]="categoryOptions"
        [selectedValue]="editData.categoryID != null ? String(editData.categoryID) : null"
        placeholder="Select category"
        [allowAddNew]="true"
        [addNewLabel]="'Add new category'"
        (selectedValueChange)="editData.categoryID = fromValue($event)"
        (addMore)="onAddNewCategory()">
      </app-single-select-dropdown>
    </div>
    <div class="form-group-mobile">
      <label class="label-with-tip">Price
        <app-tooltip [tooltipId]="'mi-svc-price'">
          <i icon class="bi bi-info-circle"></i>
          <div content><p>The price you charge customers for one unit of this service.</p></div>
        </app-tooltip>
      </label>
      <input type="number" class="form-control" [(ngModel)]="editData.sellingPrice" min="0" step="0.01" />
    </div>
    <!-- A Service is quantified as time, so the unit is fixed to Minutes and
         the user enters how long it takes. -->
    <div class="form-group-mobile">
      <label class="label-with-tip">Time (Minutes) *
        <app-tooltip [tooltipId]="'mi-svc-time'">
          <i icon class="bi bi-info-circle"></i>
          <div content><p>How long this service takes to deliver, in minutes.</p></div>
        </app-tooltip>
      </label>
      <input type="number" class="form-control" min="1" step="1"
             [(ngModel)]="editData.purchaseQuantity" name="svcMinutes" placeholder="e.g. 30" />
    </div>

    <div class="form-group-mobile">
      <label class="label-with-tip">Unit of Measure
        <app-tooltip [tooltipId]="'mi-svc-uom'">
          <i icon class="bi bi-info-circle"></i>
          <div content><p>Services are billed in minutes.</p></div>
        </app-tooltip>
      </label>
      <input type="text" class="form-control" [value]="minutesUomName" readonly />
    </div>

  </ng-container>

</app-offcanvas-sheet>
